import { Skeleton } from "./skeleton";

interface DashboardSkeletonProps {
  showStats?: boolean;
  showRecentActivity?: boolean;
  showSystemHealth?: boolean;
  showStudents?: boolean;
  showPrograms?: boolean;
}

export function DashboardSkeleton({
  showStats = true,
  showRecentActivity = true,
  showSystemHealth = true,
  showStudents = true,
  showPrograms = true,
}: DashboardSkeletonProps) {
  return (
    <div className="flex flex-col gap-4 p-4 md:p-8">
      {/* Header Skeleton */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-3 w-32" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-32" />
        </div>
      </div>

      {/* Tabs Skeleton */}
      <div className="flex justify-between">
        <Skeleton className="h-10 w-96" />
        <Skeleton className="h-10 w-48" />
      </div>

      {/* Stats Grid Skeleton */}
      {showStats && (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5">
          {Array(5).fill(null).map((_, index) => (
            <div key={index} className="rounded-lg border p-4 shadow-sm">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-4" />
                </div>
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent Activity and System Health Grid */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-7">
        {/* Recent Activity Skeleton */}
        {showRecentActivity && (
          <div className="lg:col-span-4 rounded-lg border p-4 shadow-sm">
            <div className="space-y-4">
              <div>
                <Skeleton className="h-6 w-32 mb-2" />
                <Skeleton className="h-4 w-48" />
              </div>
              <div className="space-y-4">
                {Array(5).fill(null).map((_, index) => (
                  <div key={index} className="flex items-center space-x-4">
                    <Skeleton className="h-8 w-8 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-3 w-32" />
                    </div>
                    <Skeleton className="h-5 w-16" />
                  </div>
                ))}
              </div>
              <Skeleton className="h-9 w-full" />
            </div>
          </div>
        )}

        {/* System Health Skeleton */}
        {showSystemHealth && (
          <div className="lg:col-span-3 rounded-lg border p-4 shadow-sm">
            <div className="space-y-4">
              <div>
                <Skeleton className="h-6 w-28 mb-2" />
                <Skeleton className="h-4 w-40" />
              </div>
              <div className="space-y-3">
                {Array(4).fill(null).map((_, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-4 w-16" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Students Section Skeleton */}
      {showStudents && (
        <div className="rounded-lg border p-4 shadow-sm">
          <div className="space-y-4">
            <div>
              <Skeleton className="h-6 w-24 mb-2" />
              <Skeleton className="h-4 w-48" />
            </div>
            <div className="space-y-3">
              {Array(3).fill(null).map((_, index) => (
                <div key={index} className="flex items-center space-x-4 p-3 border rounded">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-6 w-16" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Programs Section Skeleton */}
      {showPrograms && (
        <div className="rounded-lg border p-4 shadow-sm">
          <div className="space-y-4">
            <div>
              <Skeleton className="h-6 w-28 mb-2" />
              <Skeleton className="h-4 w-44" />
            </div>
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {Array(6).fill(null).map((_, index) => (
                <div key={index} className="rounded-lg border p-3">
                  <div className="space-y-3">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-2/3" />
                    <div className="flex gap-2 pt-2">
                      <Skeleton className="h-6 w-16" />
                      <Skeleton className="h-6 w-16" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
